<script lang="ts" setup>
  import { computed, ref, watch } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { useValidation, useValidators } from '@/shared/composables'
  import { useCommonStore, useFeaturesStore, useUserStore } from '@/shared/store'
  import {
    DEFAULT_MAX_LENGTH,
    NAME_MASK_CONFIG,
    PASSPORT_MASK_CONFIG,
    PASSPORT_MAX_LENGTH,
  } from '@/shared/constants/masks.constants'
  import FlowCardQC from '@/shared/components/FlowCardQC/FlowCardQC.vue'
  import type { TravelerInformation as ApiTravelerInformation } from '@/shared/types/api.types'
  import { getEmptyTravelerObject } from './FlowTravelerFormWidgetQC.utils'
  import FlowDataProtectReminderQC from '@/entities/Flow/components/FlowDataProtectReminderQC/FlowDataProtectReminderQC.vue'
  import { TravelDirection, type Locale } from '@/shared/types/common.types'
  import {
    INBOUND_MAX_AGE,
    OUTBOUND_MAX_AGE,
    DEFAULT_START_DATE,
  } from '@/shared/constants/flow.constants'
  import { decreaseYears, formatDate, parseDateFromStr } from '@/shared/utils'
  import ButtonQC from '@qicgroupdoha/qic-connect-ds/components/ButtonQC'
  import SelectQC from '@qicgroupdoha/qic-connect-ds/components/SelectQC'
  import InputQC from '@qicgroupdoha/qic-connect-ds/components/InputQC'
  import InputDateQC from '@qicgroupdoha/qic-connect-ds/components/InputDateQC'
  import WrapperQC from '@qicgroupdoha/qic-connect-ds/components/WrapperQC'

  interface Props {
    information?: ApiTravelerInformation | null
    withWinterSport?: boolean
    typeOfTravel?: TravelDirection
    isLoading?: boolean
    withBackButton?: boolean
  }

  interface Emits {
    (e: 'save', information: ApiTravelerInformation): void
    (e: 'back'): void
  }

  const props = withDefaults(defineProps<Props>(), {
    information: null,
    withWinterSport: false,
    typeOfTravel: TravelDirection.OUTBOUND,
    isLoading: false,
    withBackButton: false,
  })

  const emit = defineEmits<Emits>()

  const { t, locale } = useI18n()

  const nowDate = new Date()
  const nationalities = computed(() => commonStore.nationalities)
  const relations = computed(() => commonStore.relations)

  const userStore = useUserStore()
  const featuresStore = useFeaturesStore()
  const isGeorgiaWinterCoverEnabled = computed(() => featuresStore.isGeorgiaWinterCoverEnabled)

  const commonStore = useCommonStore()
  const isEditMode = !!props.information

  const title = computed(() =>
    isEditMode ? t('travellers_update_info') : t('travellers_add_person')
  )

  const travelerInformation = ref<ApiTravelerInformation>(getEmptyTravelerObject())
  const nationality = ref(props.information?.nationalityCode || '')
  const firstName = ref(props.information?.firstName || '')
  const lastName = ref(props.information?.lastName || '')
  const relation = ref(props.information?.relationCode || '')
  const dateOfBirth = ref(
    isEditMode && props.information?.dateOfBirth
      ? parseDateFromStr(props.information.dateOfBirth)
      : null
  )
  const rejectedDateOfBirth = ref<Date | null>(null)
  watch(dateOfBirth, () => (rejectedDateOfBirth.value = null))
  const validatedDateOfBirth = computed(() => dateOfBirth.value ?? rejectedDateOfBirth.value)
  const passportNumber = ref(props.information?.passportNo || '')
  if (props.information) {
    travelerInformation.value = {
      ...props.information,
    }
  }
  const isOutbound = computed(() => props.typeOfTravel === TravelDirection.OUTBOUND)
  const maxAge = computed(() => (isOutbound.value ? OUTBOUND_MAX_AGE : INBOUND_MAX_AGE))

  const MIN_DATE_OF_BIRTH = computed(() => decreaseYears(nowDate, maxAge.value))

  const getWinSportsValue = (): number => {
    if (isGeorgiaWinterCoverEnabled.value && userStore.isGeorgiaDestination) {
      return props.information?.winSports !== undefined ? +props.information.winSports : 1
    }

    if (props.withWinterSport && props.information?.winSports !== undefined) {
      return +props.information.winSports
    }

    return 0
  }

  const onSave = () => {
    v$.value.$touch()
    if (v$.value.$error) return

    const oldInformation = props.information || getEmptyTravelerObject()
    const newInformation = {
      ...oldInformation,
      nationalityCode: nationality.value,
      nationalityName: String(
        nationalities.value.find(
          (nationalityValue) => nationalityValue.nationalityCode === nationality.value
        )?.nationalityName
      ),
      relationCode: relation.value,
      firstName: firstName.value.trim(),
      lastName: lastName.value.trim(),
      passportNo: passportNumber.value,
      relationName: String(
        relations.value.find((relationValue) => relationValue.code === relation.value)?.desc
      ),
      dateOfBirth: dateOfBirth.value ? formatDate(dateOfBirth.value, 'en', 'dd/MM/yyyy') : '',
      winSports: getWinSportsValue(),
    }

    emit('save', newInformation)
  }

  const { required, maxAgeValidator } = useValidators()
  const validationRules = computed(() => ({
    relation: { required },
    firstName: { required },
    lastName: { required },
    passportNumber: { required },
    nationality: { required },
    dateOfBirth: {
      required,
      maxAge: maxAgeValidator(isOutbound.value, MIN_DATE_OF_BIRTH.value),
    },
  }))

  const { v$, errors } = useValidation(validationRules, {
    relation,
    firstName,
    lastName,
    passportNumber,
    nationality,
    dateOfBirth: validatedDateOfBirth,
  })
</script>

<template>
  <FlowCardQC :title="title" :with-back-button="withBackButton" @back="emit('back')">
    <WrapperQC direction="column" gap="var(--spacing-5)">
      <SelectQC
        v-model="relation"
        data-test="relation-input"
        :container-attrs="{
          'data-test': 'relation-input',
        }"
        :options="relations"
        title-key="desc"
        value-key="code"
        :locale="locale as Locale"
        :label="t('inbound_travelers_add_relationship')"
        is-filterable
        :loading="isLoading || !relations.length"
        :errors="errors.relation"
      />

      <InputQC
        v-model="firstName"
        :mask-config="NAME_MASK_CONFIG"
        :max-length="DEFAULT_MAX_LENGTH"
        :label="t('inbound_travelers_add_first_name')"
        :errors="errors.firstName"
        :loading="isLoading"
        :container-attrs="{
          'data-test': 'first-name-input',
        }"
        :is-filled="!v$.firstName.$invalid"
        data-test="first-name-input-inner"
      />

      <InputQC
        v-model="lastName"
        :mask-config="NAME_MASK_CONFIG"
        :max-length="DEFAULT_MAX_LENGTH"
        :label="t('inbound_travelers_add_last_name')"
        :errors="errors.lastName"
        :loading="isLoading"
        :container-attrs="{
          'data-test': 'last-name-input',
        }"
        data-test="last-name-input-inner"
        :is-filled="!v$.lastName.$invalid"
      />

      <!-- @vue-ignore -->
      <InputDateQC
        v-model="dateOfBirth"
        :min-date="MIN_DATE_OF_BIRTH"
        :max-date="nowDate"
        :locale="locale"
        :label="t('inbound_travelers_add_dob')"
        :errors="errors.dateOfBirth"
        :loading="isLoading"
        :calendar-props="{
          startDate: dateOfBirth ?? DEFAULT_START_DATE,
        }"
        :input-props="{
          containerAttrs: {
            'data-test': 'birth-date-traveler',
          },
          isFilled: !v$.dateOfBirth.$invalid,
        }"
        allow-manual-input
        data-test="birth-date-traveler-inner"
        @invalid-date="rejectedDateOfBirth = $event"
      />

      <SelectQC
        v-model="nationality"
        :options="nationalities"
        title-key="nationalityName"
        value-key="nationalityCode"
        :label="t('inbound_travelers_add_nationality')"
        :errors="errors.nationality"
        :loading="isLoading"
        :locale="locale as Locale"
        :container-attrs="{
          'data-test': 'nationality-input-traveler',
        }"
        data-test="nationality-input-traveler-inner"
        is-filterable
      />

      <InputQC
        v-model="passportNumber"
        :label="t('inbound_travelers_add_passport')"
        :mask-config="PASSPORT_MASK_CONFIG"
        :max-length="PASSPORT_MAX_LENGTH"
        :loading="isLoading"
        :container-attrs="{
          'data-test': 'traveler-passport-number-input',
        }"
        :errors="errors.passportNumber"
        :is-filled="!v$.passportNumber.$invalid"
        data-test="traveler-passport-number-input-inner"
      />
    </WrapperQC>

    <template #button>
      <ButtonQC :loading="isLoading" @click="onSave">
        {{ isEditMode ? $t('inbound_travelers_edit_save_button') : $t('travelers.add_button') }}
      </ButtonQC>
    </template>

    <template #after>
      <FlowDataProtectReminderQC />
    </template>
  </FlowCardQC>
</template>
